import FusionCustomInput from 'components/FusionCustomInput/FusionCustomInput';
import { Form } from 'react-bootstrap';
import { formatDateTimeLocalInput, formatTimeZoneWithOffset } from 'utils/dates/date.utils';
import {
  getIpawsDurationLabel,
  getIpawsExpirationError,
  getMinimumIpawsExpirationTime,
} from '../../../../utils/alertCreator.utils';
import {
  getIpawsUpdateIntervalOptions,
  hasWeaDeliveryChannel,
  IPAWS_WEA_INTERVAL_ISSUE,
  IPAWS_WEA_INTERVAL_NOTE,
  isIntervalAboveWeaLimit,
} from '../../../../utils/ipawsUpdateInterval.utils';
import { joinDescriptionIds } from '../../utils/ipawsAlertFields.utils';
import IpawsDateTimeField from '../IpawsDateTimeField/IpawsDateTimeField';
import IpawsFieldFindings, {
  getIpawsFieldFindingDescriptionId,
} from '../IpawsFieldFindings/IpawsFieldFindings';

const MILLISECONDS_PER_MINUTE = 60 * 1000;

/** Keeps a saved interval that is not in the option list selectable. */
const getIntervalOptions = (intervalDuration, deliveryChannels) => {
  const options = getIpawsUpdateIntervalOptions(deliveryChannels);
  return intervalDuration > 0 && !options.some(({ value }) => value === intervalDuration)
    ? [
        ...options,
        {
          value: intervalDuration,
          label: `${Math.round(intervalDuration / MILLISECONDS_PER_MINUTE)} minutes (saved value)`,
        },
      ]
    : options;
};

/**
 * Global Action timing (card BE-25 / FE-12). The Global Actions service sets the effective and
 * expiration times while the event is active, so the operator sets only the update interval
 * and an optional expected onset.
 */
const IpawsActiveEventTimingFields = ({ draft, findings, onDraftChange, onDateTimeChange }) => {
  const intervalDuration = Number(draft.intervalDuration);
  const isWeaSelected = hasWeaDeliveryChannel(draft.deliveryChannels);
  // With WEA selected, an interval above the WEA limit is not offered and does not count.
  const isAboveWeaLimit = isIntervalAboveWeaLimit(intervalDuration, draft.deliveryChannels);
  const hasValidInterval =
    Number.isInteger(intervalDuration) && intervalDuration > 0 && !isAboveWeaLimit;

  return (
    <>
      <FusionCustomInput
        controlId="ipaws-action-interval-duration"
        label="Active event update interval"
        required
        fullWidth
      >
        <Form.Select
          className="alert-creator-control"
          data-bs-theme="dark"
          name="intervalDuration"
          value={hasValidInterval ? intervalDuration : ''}
          onChange={(event) => onDraftChange({ intervalDuration: Number(event.target.value) })}
          required
          isInvalid={!hasValidInterval}
          aria-invalid={!hasValidInterval}
          aria-describedby={joinDescriptionIds(
            'ipaws-action-interval-duration-help',
            isWeaSelected ? 'ipaws-action-interval-duration-wea' : undefined,
            hasValidInterval ? undefined : 'ipaws-action-interval-duration-error',
          )}
        >
          <option value="" disabled>
            Select an interval
          </option>
          {getIntervalOptions(hasValidInterval ? intervalDuration : 0, draft.deliveryChannels).map(
            ({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ),
          )}
        </Form.Select>
        {hasValidInterval ? null : (
          <Form.Control.Feedback
            id="ipaws-action-interval-duration-error"
            type="invalid"
            className="d-block"
          >
            {isAboveWeaLimit
              ? IPAWS_WEA_INTERVAL_ISSUE
              : 'Select the active event update interval.'}
          </Form.Control.Feedback>
        )}
        <small id="ipaws-action-interval-duration-help" className="d-block mt-1">
          Time between IPAWS updates while the event is active. The Global Actions service sets the
          expiration while the event is active: each update extends it by this interval.
        </small>
        {isWeaSelected ? (
          <small id="ipaws-action-interval-duration-wea" className="d-block mt-1">
            {IPAWS_WEA_INTERVAL_NOTE}
          </small>
        ) : null}
      </FusionCustomInput>

      <IpawsDateTimeField
        controlId="ipaws-alert-onset"
        fieldPath="onset"
        label="Expected onset (informational, optional)"
        value={draft.onset}
        onChange={onDateTimeChange}
        findings={findings}
      />
    </>
  );
};

const IpawsTimingSection = ({
  draft,
  findings,
  onDraftChange,
  onDateTimeChange,
  expiryLimits,
  showIntervalDuration = false,
}) => {
  if (showIntervalDuration) {
    return (
      <fieldset className="alert-creator-section alert-creator-section-ipaws ipaws-timing border rounded-3 p-3 shadow-sm">
        <legend className="alert-creator-section-title d-flex align-items-center">Timing</legend>
        <IpawsActiveEventTimingFields
          draft={draft}
          findings={findings}
          onDraftChange={onDraftChange}
          onDateTimeChange={onDateTimeChange}
        />
      </fieldset>
    );
  }

  return (
    <IpawsScheduledTimingSection
      draft={draft}
      findings={findings}
      onDraftChange={onDraftChange}
      onDateTimeChange={onDateTimeChange}
      expiryLimits={expiryLimits}
    />
  );
};

/** Alert Creator timing: the operator sets the effective, onset and expiration times. */
const IpawsScheduledTimingSection = ({
  draft,
  findings,
  onDraftChange,
  onDateTimeChange,
  expiryLimits,
}) => {
  const expirationError = getIpawsExpirationError(draft, new Date(), expiryLimits);
  const durationLabel = getIpawsDurationLabel(draft);
  const minimumExpirationTime = getMinimumIpawsExpirationTime(draft.effective);
  const expirationFindingId = getIpawsFieldFindingDescriptionId('expires', findings);
  const hasExpirationConfirmationFinding = Boolean(!expirationError && expirationFindingId);

  return (
    <fieldset className="alert-creator-section alert-creator-section-ipaws ipaws-timing border rounded-3 p-3 shadow-sm">
      <legend className="alert-creator-section-title d-flex align-items-center">Timing</legend>

      <IpawsDateTimeField
        controlId="ipaws-alert-onset"
        fieldPath="onset"
        label="Expected onset (informational)"
        value={draft.onset}
        onChange={onDateTimeChange}
        findings={findings}
        minimum={draft.effective}
        timeZoneValue={draft.onset || draft.effective}
      />

      <FusionCustomInput controlId="ipaws-alert-expires" label="Expiration time" required fullWidth>
        <Form.Control
          className="alert-creator-control"
          type="datetime-local"
          name="expires"
          value={formatDateTimeLocalInput(draft.expires)}
          onChange={onDateTimeChange}
          min={formatDateTimeLocalInput(minimumExpirationTime)}
          isInvalid={Boolean(expirationError)}
          aria-invalid={Boolean(expirationError)}
          aria-describedby={joinDescriptionIds(
            'ipaws-alert-expires-zone',
            expirationError ? 'ipaws-alert-expires-error' : 'ipaws-alert-expires-duration',
          )}
          required
        />
        <small id="ipaws-alert-expires-zone" className="d-block mt-1">
          {formatTimeZoneWithOffset(draft.expires)}
        </small>
        {expirationError ? (
          <Form.Control.Feedback id="ipaws-alert-expires-error" type="invalid" className="d-block">
            {expirationError}
          </Form.Control.Feedback>
        ) : (
          <small id="ipaws-alert-expires-duration" className="d-block fw-semibold mt-1">
            {durationLabel}
          </small>
        )}
      </FusionCustomInput>

      <div className="alert-creator-option-row d-flex flex-wrap align-items-center gap-3 border rounded-3 p-3">
        <Form.Check
          id="ipaws-alert-confirm-expiration"
          type="checkbox"
          label="I reviewed the expiration time and duration"
          checked={draft.hasConfirmedExpires}
          onChange={(event) => onDraftChange({ hasConfirmedExpires: event.target.checked })}
          disabled={Boolean(expirationError)}
          required
          aria-required="true"
          aria-invalid={hasExpirationConfirmationFinding}
          isInvalid={hasExpirationConfirmationFinding}
          aria-describedby={hasExpirationConfirmationFinding ? expirationFindingId : undefined}
        />
      </div>
      {hasExpirationConfirmationFinding && (
        <IpawsFieldFindings fieldPath="expires" findings={findings} />
      )}
    </fieldset>
  );
};

export default IpawsTimingSection;
